import React, { useState, useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import Markup from 'components/Markup';
import Icon from 'components/UI/Icon';
import { getHeaderDeviceType } from 'utils';
import { useTrans } from 'utils/localisation';
import { pushSearchEvent } from 'utils/analytics';
import { EMOJI_REGEX, SPECIAL_SYMBOLS_REGEX } from 'define/regex';

import * as styles from './SearchInput.module.scss';

const SearchInput = ({
  className,
  placeholder,
  search,
  results,
  clearSearch,
  handleSearch,
  handleItemClick,
  handleKeyDown,
  onInputClick,
  showList,
  loading,
}) => {
  const t = useTrans(['common', 'validation']);
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [errorStyle, setErrorStyle] = useState({ '--width': 'fit-content', '--translateX': 0 });
  const errorRef = useRef();
  const errorTitleRef = useRef();

  const validateInput = useCallback(
    value => {
      if (EMOJI_REGEX.test(value)) {
        return t('noEmoji');
      }
      if (SPECIAL_SYMBOLS_REGEX.test(value)) {
        return t('noSpecialSymbols');
      }
      return '';
    },
    [t],
  );

  const animateError = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 300);
  }, []);

  useEffect(() => {
    if (error && errorRef.current && errorTitleRef.current) {
      if (errorRef.current.clientWidth < errorTitleRef.current.clientWidth) {
        setErrorStyle({
          '--width': 'auto',
          '--translateX': `${errorRef.current.clientWidth - errorTitleRef.current.clientWidth}px`,
        });
      } else {
        setErrorStyle({ '--width': 'fit-content', '--translateX': 0 });
      }
    }
  }, [error]);

  const handleInputChange = useCallback(
    e => {
      const value = e.target.value;
      const validationError = validateInput(value);

      if (validationError) {
        setError(validationError);
        animateError();
      } else {
        setError('');
      }

      handleSearch(e);
    },
    [handleSearch, validateInput, animateError],
  );

  const handleSuggestionClick = useCallback(
    value => {
      const deviceType = getHeaderDeviceType(window.innerWidth);
      handleItemClick(value);
      pushSearchEvent({
        action: 'click_on_suggestion',
        label: value,
        deviceType,
        pageUrl: window.location.href,
      });
    },
    [handleItemClick],
  );

  const handleClearSearch = useCallback(() => {
    setError('');
    clearSearch();
  }, [clearSearch]);

  const handleKeyDownWithValidation = useCallback(
    e => {
      const validationError = validateInput(search);

      if (validationError && e.key === 'Enter') {
        e.preventDefault();
        setError(validationError);
        animateError();
        return;
      }

      handleKeyDown(e);
    },
    [search, validateInput, animateError, handleKeyDown],
  );

  const toggleFocus = useCallback(() => {
    setFocused(prev => !prev);
  }, []);
  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={cn(styles.searchRow, focused && styles.active)}>
        <Icon name="loupe-left-side" className={styles.searchIcon} />
        {search && (
          <Icon
            role="button"
            name="close"
            className={styles.closeIcon}
            onClick={handleClearSearch}
          />
        )}
        <input
          name="Search"
          type="search"
          maxLength="256"
          placeholder={placeholder ?? t('searchPlaceholder')}
          className={styles.searchInput}
          value={search}
          onClick={onInputClick}
          onChange={handleInputChange}
          onKeyDown={handleKeyDownWithValidation}
          onFocus={toggleFocus}
          onBlur={toggleFocus}
          disabled={loading}
        />
        {error && (
          <div
            className={cn(styles.error, shake && styles.shake, error && styles.errorAnimation)}
            ref={errorRef}
          >
            <div className={styles.errorTitle} ref={errorTitleRef} style={errorStyle}>
              {error}
            </div>
          </div>
        )}
      </div>
      {showList && results?.length > 0 && (
        <div className={styles.listWrapper}>
          <ul className={styles.list}>
            {results.map(item => (
              <Markup
                tag="li"
                key={item}
                className={styles.listItem}
                onClick={() => handleSuggestionClick(item)}
              >
                {item}
              </Markup>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
export default SearchInput;
